import React, { useState } from "react";
import { Loader2, Rocket, Undo2, Archive, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import WearableStatusBadge from "./WearableStatusBadge";
import ModalShell from "./ModalShell";
import { TextAreaField } from "./FormField";
import {
  getLifecycleActions,
  getStatusMeta,
  getActionLabel,
  TERMINAL_STATUSES,
  formatApiError,
} from "./constants";

// Icon + intent per lifecycle action. `validated` is never an action here (it
// is earned via the checklist, not settable).
const ACTION_META = {
  deployed: { Icon: Rocket, tone: "primary" },
  returned: { Icon: Undo2, tone: "neutral" },
  retired: { Icon: Archive, tone: "neutral" },
  scrapped: { Icon: Trash2, tone: "danger" },
};

/**
 * Post-validation lifecycle control. While a device is `in_validation` the only
 * available action is Scrap (validated is earned by completing the checklist,
 * never set here). Once validated, the mapped moves appear. Scrap always needs
 * a reason, so it opens a modal; every other move is a direct PATCH.
 */
const StatusControl = ({ wearable, onUpdated }) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(null);
  const [showScrap, setShowScrap] = useState(false);
  const [scrapReason, setScrapReason] = useState("");
  const [scrapping, setScrapping] = useState(false);
  const [scrapError, setScrapError] = useState(null);

  const actions = getLifecycleActions(wearable.status);
  const isTerminal = TERMINAL_STATUSES.includes(wearable.status);
  const inValidation = wearable.status === "in_validation";

  const applyStatus = async (status) => {
    setPending(status);
    try {
      const res = await betaAPI.updateWearable({
        bleMac: wearable.ble_mac,
        body: { status },
      });
      showToast.success(
        t("admin.manufacturing.statusControl.movedTo", {
          status: getStatusMeta(status).label,
        })
      );
      onUpdated?.(res.data);
    } catch (err) {
      showToast.error(
        formatApiError(err, t("admin.manufacturing.statusControl.updateFailed"))
      );
    } finally {
      setPending(null);
    }
  };

  const handleScrap = async () => {
    if (!scrapReason.trim()) return;
    setScrapping(true);
    setScrapError(null);
    try {
      const res = await betaAPI.updateWearable({
        bleMac: wearable.ble_mac,
        body: { status: "scrapped", scrap_reason: scrapReason.trim() },
      });
      showToast.success(t("admin.manufacturing.statusControl.scrapped"));
      onUpdated?.(res.data);
      setShowScrap(false);
      setScrapReason("");
    } catch (err) {
      setScrapError(
        formatApiError(err, t("admin.manufacturing.statusControl.scrapFailed"))
      );
    } finally {
      setScrapping(false);
    }
  };

  const renderAction = (status) => {
    const { Icon, tone } = ACTION_META[status] || {};
    const busy = pending === status;
    const label = getActionLabel(status) || getStatusMeta(status).label;

    if (status === "scrapped") {
      return (
        <button
          key={status}
          onClick={() => setShowScrap(true)}
          disabled={Boolean(pending)}
          className="flex items-center gap-1.5 rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3.5 py-2 text-sm font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEE2E2] disabled:opacity-60"
        >
          <Trash2 size={14} />
          {label}
        </button>
      );
    }

    const primary = tone === "primary";
    return (
      <button
        key={status}
        onClick={() => applyStatus(status)}
        disabled={Boolean(pending)}
        className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition duration-150 disabled:opacity-60 ${
          primary
            ? "text-white"
            : "border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
        }`}
        style={
          primary
            ? {
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }
            : undefined
        }
      >
        {busy ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          Icon && <Icon size={14} />
        )}
        {label}
      </button>
    );
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("admin.manufacturing.statusControl.title")}
        </h2>
        <WearableStatusBadge status={wearable.status} size="md" />
      </div>

      {wearable.status === "scrapped" && wearable.scrap_reason && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-3 py-2.5 text-xs text-[#991B1B]">
          <span className="font-medium">
            {t("admin.manufacturing.statusControl.scrapReasonLabel")}
          </span>{" "}
          {wearable.scrap_reason}
        </div>
      )}

      {isTerminal ? (
        <p className="text-xs text-[#9CA3AF]">
          {t("admin.manufacturing.statusControl.terminal")}
        </p>
      ) : (
        <>
          {inValidation && (
            <p className="mb-3 text-xs text-[#6B7280]">
              {t("admin.manufacturing.statusControl.inValidationHint")}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {actions.map(renderAction)}
          </div>
        </>
      )}

      <ModalShell
        show={showScrap}
        onClose={() => {
          setShowScrap(false);
          setScrapReason("");
          setScrapError(null);
        }}
        icon={Trash2}
        title={t("admin.manufacturing.statusControl.scrapTitle")}
        subtitle={t("admin.manufacturing.statusControl.scrapSubtitle", {
          serial: wearable.serial,
        })}
        submitLabel={t("admin.manufacturing.statusControl.scrapTitle")}
        submitting={scrapping}
        submitDisabled={!scrapReason.trim()}
        onSubmit={handleScrap}
        error={scrapError}
        destructive
        maxWidth="460px"
      >
        <TextAreaField
          label={t("admin.manufacturing.statusControl.scrapReason")}
          value={scrapReason}
          onChange={setScrapReason}
          placeholder={t(
            "admin.manufacturing.statusControl.scrapReasonPlaceholder"
          )}
          required
          rows={3}
        />
      </ModalShell>
    </div>
  );
};

export default StatusControl;
